/**
 * 租戶主題設定（tenant_sys_profile · porsche 2026-07-30）
 * porsche 拍板：只能設**自己所屬租戶**（無跨租戶下拉）。
 * 介面沿用 SysProfilePage 卡片式設計（主題色迷你 UI 預覽 / 版面縮圖）。
 * 6 區：主題色 / 版面 / Logo(+favicon) / 登入背景 / 系統名稱(依 tenant_languages) / 時區。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMessage } from '../contexts/MessageContext';
import { useSystem } from '../contexts/SystemContext';
import { usePermission } from '../hooks/usePermission';
import FunctionPageHeader from '../components/FunctionPageHeader';
import {
  TenantSysProfile, brandingImageUrl, getTenantSysProfile,
  updateTenantSysProfile, uploadTenantLogo, uploadTenantLoginBg,
} from '../services/tenantSysProfileService';
import '../styles/DataTable.css';

const FUNC = 'tenant_sys_profile';

// 主題色 preset（迷你 UI 預覽用色，對齊 SysProfilePage 內頁配色卡）
const THEME_PRESETS = [
  { key: 'blue', label: '藍', sidebarColor: '#1a3a5c', accentColor: '#1976d2', accentEndColor: '#42a5f5' },
  { key: 'green', label: '綠', sidebarColor: '#1b3d2f', accentColor: '#2e7d32', accentEndColor: '#66bb6a' },
  { key: 'black', label: '黑', sidebarColor: '#212121', accentColor: '#424242', accentEndColor: '#616161' },
  { key: 'coffee', label: '咖啡', sidebarColor: '#3e2723', accentColor: '#6d4c41', accentEndColor: '#8d6e63' },
];

const FALLBACK_TZ = [
  'Asia/Taipei', 'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Asia/Singapore',
  'Asia/Seoul', 'UTC', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Europe/Paris',
];
const ALL_TIMEZONES: string[] = (() => {
  try {
    const fn = (Intl as any).supportedValuesOf;
    if (typeof fn === 'function') return fn('timeZone');
  } catch { /* silent ok: 舊瀏覽器無 supportedValuesOf → 用 fallback */ }
  return FALLBACK_TZ;
})();

const langLabel = (code: string): string => {
  const map: Record<string, string> = { 'zh-TW': '繁體中文', 'zh-CN': '简体中文', en: 'English' };
  return map[code] || code;
};

const cardWrap = (selected: boolean, canUpdate: boolean): React.CSSProperties => ({
  cursor: canUpdate ? 'pointer' : 'default',
  border: selected ? '3px solid #333' : '3px solid transparent',
  borderRadius: '10px',
  overflow: 'hidden',
  transition: 'all 0.2s ease',
  opacity: canUpdate ? 1 : 0.7,
  boxShadow: selected ? '0 4px 12px rgba(0,0,0,0.2)' : '0 2px 6px rgba(0,0,0,0.1)',
});
const cardLabel = (selected: boolean): React.CSSProperties => ({
  textAlign: 'center', padding: '6px 0', fontSize: '13px',
  fontWeight: selected ? 600 : 400, color: selected ? '#333' : '#495057', background: '#fff',
});
const sectionBox: React.CSSProperties = {
  background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px',
};
const sectionLabel: React.CSSProperties = {
  fontWeight: 600, fontSize: '15px', marginBottom: '12px', display: 'block',
};

const TenantSysProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();
  const { loadTenantBranding } = useSystem();   // 儲存/上傳後 re-fetch → 全站主題/logo/名稱即時生效

  const canRead = hasPermission(FUNC, 'read');
  const canUpdate = hasPermission(FUNC, 'update');

  const [profile, setProfile] = useState<TenantSysProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const bgRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProfile(await getTenantSysProfile());
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [showApiError]);

  useEffect(() => {
    if (!permLoading && canRead) load();
  }, [permLoading, canRead, load]);

  const setField = <K extends keyof TenantSysProfile>(k: K, v: TenantSysProfile[K]) =>
    setProfile((p) => (p ? { ...p, [k]: v } : p));

  const handleSave = async () => {
    if (!profile || !canUpdate) return;
    setSaving(true);
    try {
      const updated = await updateTenantSysProfile({
        theme_color: profile.theme_color,
        layout_mode: profile.layout_mode,
        system_name: profile.system_name,
        tenant_copyright: profile.tenant_copyright,
        timezone: profile.timezone,
      });
      setProfile(updated);
      await loadTenantBranding();   // 即時套用主題/版面/名稱到全站（不需重整）
      showSuccess('SYS020001', { name: t('tenantSysProfile.title', '租戶主題設定') });
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (kind: 'logo' | 'bg', file?: File) => {
    if (!file || !canUpdate) return;
    setSaving(true);
    try {
      const updated = kind === 'logo' ? await uploadTenantLogo(file) : await uploadTenantLoginBg(file);
      setProfile(updated);
      await loadTenantBranding();   // 即時套用 logo/favicon 到全站
      showSuccess('SYS020001', { name: t('tenantSysProfile.title', '租戶主題設定') });
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  if (!permLoading && !canRead) {
    return <div className="page-container"><div className="error-message">{t('common.noPermission', '無此功能權限')}</div></div>;
  }
  if (loading || !profile) {
    return <div className="page-container"><div className="loading">{t('common.loading', '載入中…')}</div></div>;
  }

  const langs = profile.tenant_languages.length ? profile.tenant_languages : ['zh-TW'];

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode={FUNC} />
      </div>

      <div className="data-table-container">
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* 1. 主題色 */}
          <div style={sectionBox}>
            <label style={sectionLabel}>{t('tenantSysProfile.themeColor', '主題色')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {THEME_PRESETS.map((opt) => {
                const sel = profile.theme_color === opt.key;
                return (
                  <label key={opt.key} style={cardWrap(sel, canUpdate)}
                         onClick={() => canUpdate && setField('theme_color', opt.key)}>
                    <div style={{ width: '180px' }}>
                      <div style={{ display: 'flex', height: '70px' }}>
                        <div style={{ width: '40px', background: opt.sidebarColor, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px 0' }}>
                          <div style={{ width: '16px', height: '2px', background: 'rgba(255,255,255,0.5)', borderRadius: '1px' }} />
                          <div style={{ width: '16px', height: '2px', background: 'rgba(255,255,255,0.5)', borderRadius: '1px' }} />
                          <div style={{ width: '16px', height: '2px', background: 'rgba(255,255,255,0.3)', borderRadius: '1px' }} />
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <div style={{ height: '18px', background: `linear-gradient(135deg, ${opt.sidebarColor}, ${opt.accentColor})` }} />
                          <div style={{ flex: 1, background: '#f5f7fa', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <div style={{ padding: '2px 10px', background: `linear-gradient(135deg, ${opt.accentColor}, ${opt.accentEndColor})`, borderRadius: '3px', fontSize: '8px', color: 'white' }}>btn</div>
                              <div style={{ padding: '2px 10px', border: `1px solid ${opt.accentColor}`, borderRadius: '3px', fontSize: '8px', color: opt.accentColor, background: 'white' }}>btn</div>
                            </div>
                            <div style={{ height: '6px', background: '#e0e0e0', borderRadius: '2px', width: '80%' }} />
                            <div style={{ height: '6px', background: '#e0e0e0', borderRadius: '2px', width: '60%' }} />
                          </div>
                        </div>
                      </div>
                      <div style={cardLabel(sel)}>{opt.label}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* 2. 版面模式 */}
          <div style={sectionBox}>
            <label style={sectionLabel}>{t('tenantSysProfile.layout', '版面模式')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {[
                { key: 'vertical', label: t('tenantSysProfile.vertical', '直式（左側選單）') },
                { key: 'horizontal', label: t('tenantSysProfile.horizontal', '橫式（頂部導覽）') },
              ].map((opt) => {
                const sel = profile.layout_mode === opt.key;
                const isVertical = opt.key === 'vertical';
                return (
                  <label key={opt.key} style={cardWrap(sel, canUpdate)}
                         onClick={() => canUpdate && setField('layout_mode', opt.key)}>
                    <div style={{ width: '220px' }}>
                      <div style={{ display: 'flex', flexDirection: isVertical ? 'row' : 'column', height: '110px', background: '#f5f7fa' }}>
                        {isVertical ? (
                          <>
                            <div style={{ width: '55px', background: '#2c3e50', padding: '8px 6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {[0.6, 0.4, 0.4, 0.4].map((o, i) => <div key={i} style={{ height: '6px', background: `rgba(255,255,255,${o})`, borderRadius: '2px' }} />)}
                            </div>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                              <div style={{ height: '22px', background: 'linear-gradient(135deg, #2c3e50, #3498db)' }} />
                              <div style={{ flex: 1, padding: '8px' }}>
                                <div style={{ height: '8px', background: '#ddd', borderRadius: '2px', marginBottom: '6px', width: '80%' }} />
                                <div style={{ height: '8px', background: '#ddd', borderRadius: '2px', width: '60%' }} />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ height: '22px', background: 'linear-gradient(135deg, #2c3e50, #3498db)' }} />
                            <div style={{ height: '20px', background: '#fff', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '10px', padding: '0 10px' }}>
                              <div style={{ height: '5px', background: '#3498db', borderRadius: '2px', width: '30px' }} />
                              {[0, 1, 2].map((i) => <div key={i} style={{ height: '5px', background: '#aaa', borderRadius: '2px', width: '30px' }} />)}
                            </div>
                            <div style={{ flex: 1, padding: '8px' }}>
                              <div style={{ height: '8px', background: '#ddd', borderRadius: '2px', marginBottom: '6px', width: '90%' }} />
                              <div style={{ height: '8px', background: '#ddd', borderRadius: '2px', width: '70%' }} />
                            </div>
                          </>
                        )}
                      </div>
                      <div style={cardLabel(sel)}>{opt.label}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#6c757d' }}>
              {t('tenantSysProfile.layoutHint', '儲存後重新載入頁面即生效')}
            </div>
          </div>

          {/* 3. Logo + favicon */}
          <div style={sectionBox}>
            <label style={sectionLabel}>{t('tenantSysProfile.logo', 'Logo（自動生成 favicon）')}</label>
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '12px', minWidth: '120px', textAlign: 'center' }}>
                {profile.logo_file_id
                  ? <img src={brandingImageUrl(profile.logo_file_id)} alt="logo" style={{ maxHeight: 60, maxWidth: 180 }} />
                  : <span style={{ color: '#adb5bd', fontSize: 13 }}>{t('tenantSysProfile.noLogo', '尚未上傳')}</span>}
              </div>
              {profile.favicon_file_id && (
                <div style={{ textAlign: 'center' }}>
                  <img src={brandingImageUrl(profile.favicon_file_id)} alt="favicon" style={{ width: 16, height: 16 }} />
                  <div style={{ fontSize: 11, color: '#6c757d' }}>favicon</div>
                </div>
              )}
              <button type="button" className="btn-secondary" disabled={!canUpdate || saving}
                      onClick={() => logoRef.current?.click()}>{t('common.upload', '上傳')}</button>
              <input ref={logoRef} type="file" hidden accept="image/*"
                     onChange={(e) => handleUpload('logo', e.target.files?.[0])} />
            </div>
          </div>

          {/* 4. 登入頁背景 */}
          <div style={sectionBox}>
            <label style={sectionLabel}>{t('tenantSysProfile.loginBg', '登入頁背景')}</label>
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px', minWidth: '160px', textAlign: 'center' }}>
                {profile.login_bg_file_id
                  ? <img src={brandingImageUrl(profile.login_bg_file_id)} alt="login-bg" style={{ maxHeight: 90, maxWidth: 240, objectFit: 'cover' }} />
                  : <span style={{ color: '#adb5bd', fontSize: 13 }}>{t('tenantSysProfile.noBg', '尚未上傳')}</span>}
              </div>
              <button type="button" className="btn-secondary" disabled={!canUpdate || saving}
                      onClick={() => bgRef.current?.click()}>{t('common.upload', '上傳')}</button>
              <input ref={bgRef} type="file" hidden accept="image/*"
                     onChange={(e) => handleUpload('bg', e.target.files?.[0])} />
            </div>
          </div>

          {/* 5. 系統名稱（依 tenant_languages） */}
          <div style={sectionBox}>
            <label style={sectionLabel}>{t('tenantSysProfile.systemName', '系統名稱')}</label>
            <div className="form-grid">
              {langs.map((lng) => (
                <div className="form-group" key={lng}>
                  <label>{langLabel(lng)}</label>
                  <input type="text" disabled={!canUpdate} value={profile.system_name?.[lng] || ''}
                         onChange={(e) => setField('system_name', { ...profile.system_name, [lng]: e.target.value })} />
                </div>
              ))}
            </div>
          </div>

          {/* 6. 時區 */}
          <div style={sectionBox}>
            <label style={sectionLabel}>{t('tenantSysProfile.timezone', '時區（日期顯示）')}</label>
            <select value={profile.timezone} disabled={!canUpdate} style={{ minWidth: 260, padding: '6px' }}
                    onChange={(e) => setField('timezone', e.target.value)}>
              {(ALL_TIMEZONES.includes(profile.timezone) ? ALL_TIMEZONES : [profile.timezone, ...ALL_TIMEZONES])
                .map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>

          {canUpdate && (
            <div>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('common.saving', '儲存中…') : t('common.save', '儲存')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantSysProfilePage;
